// Battery Saver Hook
// Provides battery-aware settings for components

import { useEffect, useState, useCallback } from 'react';
import { useSettingsStore } from '../store/settings.store';

interface BatterySaverState {
  isEnabled: boolean;
  refreshInterval: number; // in milliseconds
  reduceAnimations: boolean;
  reduceMapQuality: boolean;
  batteryLevel: number | null;
  isCharging: boolean | null;
}

interface UseBatterySaverReturn extends BatterySaverState {
  shouldReduceActivity: boolean;
  getRefreshIntervalMs: () => number;
}

export function useBatterySaver(): UseBatterySaverReturn {
  const {
    batterySaverMode,
    reduceAnimations,
    reduceMapQuality,
    getEffectiveRefreshInterval,
  } = useSettingsStore();

  const [batteryLevel, setBatteryLevel] = useState<number | null>(null);
  const [isCharging, setIsCharging] = useState<boolean | null>(null);

  // Get battery status if available
  useEffect(() => {
    const getBatteryStatus = async () => {
      try {
        if ('getBattery' in navigator) {
          // @ts-expect-error Battery API not in all TypeScript definitions
          const battery = await navigator.getBattery();

          const updateBatteryInfo = () => {
            setBatteryLevel(Math.round(battery.level * 100));
            setIsCharging(battery.charging);
          };

          updateBatteryInfo();

          battery.addEventListener('levelchange', updateBatteryInfo);
          battery.addEventListener('chargingchange', updateBatteryInfo);

          return () => {
            battery.removeEventListener('levelchange', updateBatteryInfo);
            battery.removeEventListener('chargingchange', updateBatteryInfo);
          };
        }
      } catch {
        console.log('Battery API not available');
      }
    };

    getBatteryStatus();
  }, []);

  // Get refresh interval in milliseconds
  const getRefreshIntervalMs = useCallback((): number => {
    return getEffectiveRefreshInterval() * 1000;
  }, [getEffectiveRefreshInterval]);

  // Determine if we should reduce activity based on battery
  const shouldReduceActivity =
    batterySaverMode ||
    (batteryLevel !== null && batteryLevel < 20 && !isCharging);

  return {
    isEnabled: batterySaverMode,
    refreshInterval: getEffectiveRefreshInterval(),
    reduceAnimations: reduceAnimations || shouldReduceActivity,
    reduceMapQuality: reduceMapQuality || shouldReduceActivity,
    batteryLevel,
    isCharging,
    shouldReduceActivity,
    getRefreshIntervalMs,
  };
}

// Hook to get just the animation preference
export function useReducedMotion(): boolean {
  const { reduceAnimations } = useSettingsStore();
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return reduceAnimations || prefersReducedMotion;
}

// Hook to create an auto-refresh interval
export function useAutoRefresh(
  callback: () => void | Promise<void>,
  options?: {
    enabled?: boolean;
    immediate?: boolean;
  }
) {
  const { getRefreshIntervalMs } = useBatterySaver();
  const { enabled = true, immediate = false } = options || {};

  useEffect(() => {
    if (!enabled) return;

    // Call immediately if requested
    if (immediate) {
      callback();
    }

    const intervalMs = getRefreshIntervalMs();
    const intervalId = setInterval(() => {
      callback();
    }, intervalMs);

    return () => clearInterval(intervalId);
  }, [callback, enabled, immediate, getRefreshIntervalMs]);
}

export default useBatterySaver;
